import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import modelAPI from 'app/apiModel';
import { setSnackbar } from './snackbarSlice';

// Project file tree (file-service, API v0.4): /v0.4/projects/{projectId}/files.
// Documents only. Uploads are presign -> PUT straight to S3 -> register, the
// same shape as arImageSlice / arVideoSlice, so this goes through the
// versionless modelAPI client. Objects are keyed by file id on the server, so
// rename/move is a PATCH and delete is a soft delete. No URLs come back from
// list; download mints one per click.
const base = (projectId) => `/v0.4/projects/${projectId}/files`;

const errorMessage = (err, fallback) =>
    err?.response?.data?.errors?.join(' ') || err?.response?.data?.error || err?.message || fallback;

// includeHidden asks for hidden/ too; the server returns every hidden file only to
// Admins. Refreshes after upload/rename/delete omit it and keep the last setting.
export const listProjectFiles = createAsyncThunk(
    'projectFiles/list',
    async ({ projectId, includePending = true, includeHidden }, { getState, rejectWithValue }) => {
        try {
            const params = { includePending };
            if (includeHidden ?? getState().projectFiles.includeHidden) params.visibility = 'hidden,generated,uploads';
            const { data } = await modelAPI.get(base(projectId), { params });
            return data; // { files, folders }
        } catch (err) {
            return rejectWithValue(errorMessage(err, 'Unable to load project files'));
        }
    }
);

// Plain axios: the presigned URL carries its own signature and S3 rejects a
// request that also has a Bearer header. Content-Type is signed into the URL.
const putToPresignedUrl = (file, target, onProgress) =>
    axios.put(target.presignedUrl, file, {
        headers: { 'Content-Type': target.contentType },
        onUploadProgress: (e) => {
            if (e.total) onProgress(Math.round((e.loaded * 100) / e.total));
        },
    });

export const uploadProjectFiles = createAsyncThunk(
    'projectFiles/upload',
    async ({ projectId, folder, files, overwrite = false, visibility = 'uploads' }, { dispatch, rejectWithValue }) => {
        try {
            const { data: ticket } = await modelAPI.post(`${base(projectId)}/presign`, {
                visibility,
                folder: folder || undefined,
                overwrite,
                files: files.map((f) => ({ fileName: f.name, contentType: f.type || 'application/octet-stream' })),
            });

            const results = await Promise.allSettled(
                ticket.files.map((target) => {
                    const file = files.find((f) => f.name === target.fileName);
                    const report = (pct) => dispatch(setUploadProgress({ fileName: target.fileName, progress: pct }));
                    return putToPresignedUrl(file, target, report).then(() => target.fileId);
                })
            );
            const uploaded = results.filter((r) => r.status === 'fulfilled').map((r) => r.value);
            const failed = ticket.files.filter((_, i) => results[i].status === 'rejected').map((t) => t.fileName);

            // Register only the ids whose PUT succeeded; one missing object fails the whole register.
            let registered = [];
            if (uploaded.length) {
                const { data } = await modelAPI.post(`${base(projectId)}/register`, { fileIds: uploaded });
                registered = data.files || [];
            }
            dispatch(listProjectFiles({ projectId }));

            if (failed.length) {
                const message = `Upload failed for: ${failed.join(', ')}`;
                dispatch(setSnackbar({ status: 'error', message }));
                return rejectWithValue({ message, failed, registered });
            }
            dispatch(
                setSnackbar({
                    status: 'success',
                    message: `${registered.length} file${registered.length === 1 ? '' : 's'} uploaded`,
                })
            );
            return registered;
        } catch (err) {
            // Presign 409: a live file already holds one of the paths. The dialog offers to replace.
            if (err?.response?.status === 409 && err.response.data?.conflicts) {
                return rejectWithValue({ conflicts: err.response.data.conflicts });
            }
            const message = errorMessage(err, 'Unable to upload files');
            dispatch(setSnackbar({ status: 'error', message }));
            return rejectWithValue({ message });
        }
    }
);

export const downloadProjectFile = createAsyncThunk(
    'projectFiles/download',
    async ({ projectId, fileId }, { dispatch, rejectWithValue }) => {
        try {
            const { data } = await modelAPI.get(`${base(projectId)}/${fileId}/download`, {
                params: { redirect: false },
            });
            // The presigned URL carries Content-Disposition with the display name, so no
            // `download` attribute is needed (browsers ignore it cross-origin anyway).
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = data.url;
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            return data.fileName;
        } catch (err) {
            const message = errorMessage(err, 'Unable to download file');
            dispatch(setSnackbar({ status: 'error', message }));
            return rejectWithValue(message);
        }
    }
);

export const deleteProjectFile = createAsyncThunk(
    'projectFiles/delete',
    async ({ projectId, fileId }, { dispatch, rejectWithValue }) => {
        try {
            await modelAPI.delete(`${base(projectId)}/${fileId}`);
            dispatch(listProjectFiles({ projectId }));
            return fileId;
        } catch (err) {
            const message = errorMessage(err, 'Unable to delete file');
            dispatch(setSnackbar({ status: 'error', message }));
            return rejectWithValue(message);
        }
    }
);

export const renameProjectFile = createAsyncThunk(
    'projectFiles/rename',
    async ({ projectId, fileId, fileName, folder }, { dispatch, rejectWithValue }) => {
        try {
            const body = {};
            if (fileName != null) body.fileName = fileName;
            if (folder != null) body.folder = folder; // '' moves to the root
            const { data } = await modelAPI.patch(`${base(projectId)}/${fileId}`, body);
            dispatch(listProjectFiles({ projectId }));
            return data;
        } catch (err) {
            const message =
                err?.response?.status === 409 && err.response.data?.conflicts
                    ? 'A file with that name already exists in that folder'
                    : errorMessage(err, 'Unable to rename file');
            dispatch(setSnackbar({ status: 'error', message }));
            return rejectWithValue(message);
        }
    }
);

const projectFileSlice = createSlice({
    name: 'projectFiles',
    initialState: {
        files: [],
        folders: [],
        includeHidden: false,
        loading: false,
        error: null,
        uploading: false,
        uploadProgress: {}, // fileName -> percent
    },
    reducers: {
        setUploadProgress: (state, action) => {
            state.uploadProgress[action.payload.fileName] = action.payload.progress;
        },
        clearUploadProgress: (state) => {
            state.uploadProgress = {};
        },
        clearProjectFiles: () => projectFileSlice.getInitialState(),
    },
    extraReducers: (builder) => {
        builder
            .addCase(listProjectFiles.pending, (state, action) => {
                state.loading = true;
                state.includeHidden = action.meta.arg.includeHidden ?? state.includeHidden;
            })
            .addCase(listProjectFiles.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.files = action.payload?.files || [];
                state.folders = action.payload?.folders || [];
            })
            .addCase(listProjectFiles.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || action.error?.message;
            })
            .addCase(uploadProjectFiles.pending, (state) => {
                state.uploading = true;
                state.uploadProgress = {};
            })
            .addCase(uploadProjectFiles.fulfilled, (state) => {
                state.uploading = false;
            })
            .addCase(uploadProjectFiles.rejected, (state) => {
                state.uploading = false;
            });
    },
});

export const { setUploadProgress, clearUploadProgress, clearProjectFiles } = projectFileSlice.actions;
export default projectFileSlice.reducer;
